import DatePicker from "@via/components/date-picker/date-picker.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import TimePicker, {
  type TimeValue,
} from "@via/components/time-picker/time-picker.tsx";
import ToggleButton from "@via/components/toggle-button.tsx";
import {
  combineDateAndTime,
  extractDateFromPicker,
  getUserTimezone,
  isEndAfterStart,
  MAX_MESSAGE_LENGTH,
  parseLocalDateFromYmd,
} from "@via/pages/account/admin/notification-settings/utils/date-helpers.ts";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { useSaveSystemBanner } from "@via/pages/account/admin/notification-settings/notification-settings.api.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { type JSX, useState } from "react";
import BannerPreview from "./banner-preview.tsx";

const DEFAULT_START_TIME: TimeValue = { hour: "02", minute: "00", ampm: "AM" };
const DEFAULT_END_TIME: TimeValue = { hour: "03", minute: "30", ampm: "AM" };

export default function CreateBannerForm(): JSX.Element {
  const { mutate: saveBanner, isPending: isSaving } = useSaveSystemBanner();
  const { setNotification, NOTIFICATION } = useNotifications();

  const [isEnabled, setIsEnabled] = useState(true);
  const [primaryMessage, setPrimaryMessage] = useState("");
  const [secondaryMessage, setSecondaryMessage] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState<TimeValue>(DEFAULT_START_TIME);
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState<TimeValue>(DEFAULT_END_TIME);

  const handleStartDateChange = (value: unknown) => {
    const nextStartDate = extractDateFromPicker(value);
    setStartDate(nextStartDate);

    if (!nextStartDate || !endDate) return;
    const startDateValue = parseLocalDateFromYmd(nextStartDate);
    const endDateValue = parseLocalDateFromYmd(endDate);
    if (startDateValue && endDateValue && endDateValue < startDateValue) {
      setEndDate("");
    }
  };

  const resetForm = () => {
    setIsEnabled(true);
    setPrimaryMessage("");
    setSecondaryMessage("");
    setStartDate("");
    setStartTime(DEFAULT_START_TIME);
    setEndDate("");
    setEndTime(DEFAULT_END_TIME);
  };

  const handleCreate = () => {
    const startDatetime = combineDateAndTime(
      startDate,
      startTime.hour,
      startTime.minute,
      startTime.ampm
    );
    const endDatetime = combineDateAndTime(
      endDate,
      endTime.hour,
      endTime.minute,
      endTime.ampm
    );

    if (!startDatetime || !endDatetime) {
      setNotification({
        id: "banner-validation-error",
        message: "Invalid Date/Time",
        details: "Please select valid start and end dates with times.",
        variant: NOTIFICATION.WARNING,
      });
      return;
    }

    if (!isEndAfterStart(startDate, startTime, endDate, endTime)) {
      setNotification({
        id: "banner-range-validation-error",
        message: "Invalid Date Range",
        details: "End date/time must be after the start date/time.",
        variant: NOTIFICATION.WARNING,
      });
      return;
    }

    saveBanner(
      {
        primaryMessage,
        secondaryMessage,
        startDatetime,
        endDatetime,
        isEnabled,
      },
      {
        onSuccess: () => {
          resetForm();
        },
      }
    );
  };

  const isFormValid =
    primaryMessage &&
    secondaryMessage &&
    startDate &&
    endDate &&
    isEndAfterStart(startDate, startTime, endDate, endTime);

  return (
    <div className="space-y-5">
      {/* Header Section */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Create Notification Banner
        </h2>
        <p className="text-sm text-gray-500">
          Create a new notification banner that appears at the top of the
          application.
        </p>
      </div>

      {/* Enable Banner Toggle */}
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-medium text-gray-900">
              Enable Banner
            </h3>
            <p className="text-sm text-gray-500">
              Show the notification banner to all users
            </p>
          </div>
          <ToggleButton isOn={isEnabled} onToggle={setIsEnabled} />
        </div>
      </div>

      {/* Message Inputs */}
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between">
            <label
              htmlFor="primaryMessage"
              className="block text-sm font-medium text-gray-700">
              Primary Message
            </label>
            <span className="text-xs text-gray-400">
              {primaryMessage.length}/{MAX_MESSAGE_LENGTH}
            </span>
          </div>
          <input
            type="text"
            id="primaryMessage"
            maxLength={MAX_MESSAGE_LENGTH}
            value={primaryMessage}
            onChange={e => setPrimaryMessage(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Our system will undergo maintenance."
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label
              htmlFor="secondaryMessage"
              className="block text-sm font-medium text-gray-700">
              Secondary Message
            </label>
            <span className="text-xs text-gray-400">
              {secondaryMessage.length}/{MAX_MESSAGE_LENGTH}
            </span>
          </div>
          <input
            type="text"
            id="secondaryMessage"
            maxLength={MAX_MESSAGE_LENGTH}
            value={secondaryMessage}
            onChange={e => setSecondaryMessage(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            placeholder="Some services may be temporarily unavailable. We apologize for any inconvenience."
          />
        </div>
      </div>

      {/* Date/Time Inputs */}
      <div className="space-y-2">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Start Date
            </label>
            <DatePicker
              asSingle
              useRange={false}
              value={startDate || null}
              onChange={handleStartDateChange}
              placeholder="Select start date"
            />
          </div>

          <TimePicker
            label="Start Time"
            value={startTime}
            onChange={setStartTime}
            className="mt-0"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700">
              End Date
            </label>
            <DatePicker
              asSingle
              useRange={false}
              value={endDate || null}
              onChange={value => setEndDate(extractDateFromPicker(value))}
              minDate={parseLocalDateFromYmd(startDate) ?? undefined}
              placeholder="Select end date"
            />
          </div>

          <TimePicker
            label="End Time"
            value={endTime}
            onChange={setEndTime}
            className="mt-0"
          />
        </div>
        <p className="text-xs text-gray-500">
          Times are interpreted in your local timezone ({getUserTimezone()}).
        </p>
      </div>

      {/* Preview Section */}
      <BannerPreview
        primaryMessage={primaryMessage}
        secondaryMessage={secondaryMessage}
      />

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={resetForm}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          Clear
        </button>
        <button
          type="button"
          onClick={handleCreate}
          disabled={isSaving || !isFormValid}
          className={classNames(
            "inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm",
            isSaving || !isFormValid
              ? "bg-indigo-400 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          )}>
          <Spinner loading={isSaving}>Create Notification Banner</Spinner>
        </button>
      </div>
    </div>
  );
}
